import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Alert,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import TopMargin from "../components/TopMargin";
import RowMargin from "../components/RowMargin";
import SuperAppButton from "../components/SuperAppButton";
import { fetchSecure } from "../utils/utils";
import { IoMdAttach } from "react-icons/io";
import { useMyContext } from "../MyContext";

export default function UploadFileNew(props) {
  const { userData } = useMyContext();
  const [fileName, setFileName] = useState(props.fileName || null);
  const [shortFileName, setShortFileName] = useState(props.fileName || null);
  const [fileNames, setFileNames] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isDocUploading, setDocUploading] = useState(false);
  const [disableUpload, setDisableUpload] = useState(false);
  const [formData, setFormData] = useState({
    data: {},
    status: "IDLE",
  });
  const [uploadProgress, setUploadProgress] = useState({}); // Track progress for each file

  const maxFileSizeLegacy = 32 * 1024 * 1024; // 32 MB for legacy upload
  const maxFileSize = Number(userData?.maxFileSize) || 25; // Default 25 MB
  const planLimits = { silver: 25, gold: 50, platinum: 100 };

  // Generates dynamic error message for file size limits, critical for user feedback and plan upgrade prompts
  const getFileSizeErrorMessage = (totalSizeInMB) => {
    const currentPlan = userData?.planName || "freemium";
    const upgradeOptions = Object.entries(planLimits)
      .filter(([plan, limit]) => limit > maxFileSize && plan !== currentPlan)
      .map(
        ([plan, limit]) =>
          `${plan.charAt(0).toUpperCase() + plan.slice(1)} (${limit}MB)`
      )
      .join(" or ");
    return `Total file size exceeds ${maxFileSize}MB (${totalSizeInMB.toFixed(
      2
    )}MB).${upgradeOptions ? ` Upgrade to higher plans.` : ""}`;
  };

  // Validates file size and extensions, essential for ensuring only valid files are processed
  const validateFiles = (files, allowedExtensions) => {
    const totalSizeInMB = Array.from(files).reduce(
      (sum, file) => sum + file.size / (1024 * 1024),
      0
    );
    const invalidExtension = Array.from(files).some(
      (file) =>
        !allowedExtensions.includes(file.name.toLowerCase().split(".").pop())
    );
    return { totalSizeInMB, invalidExtension };
  };

  const resetFileInput = () => {
    let randomString = Math.random().toString(36);
    setFileInputKey(randomString);
    setFileNames([]);
    setShortFileName(null);
    setFormData({ data: {}, status: "IDLE" });
    setUploadProgress({});
  };

  // Handles file selection and validation, critical for user interaction and initial file checks
  const handleInputChange = (e) => {
    const target = e.currentTarget;
    const localFileNames = [];
    let value;
    let showFileNames = true;

    if (target.type === "file") {
      value = e.target.files;
      const options =
        props.uploadConfig.options[props.formData[props.uploadConfig.key]];
      const allowedExtensions = options.allowedExtensions;
      const { totalSizeInMB, invalidExtension } = validateFiles(
        value,
        allowedExtensions
      );

      if (invalidExtension) {
        setFormData({
          data: {
            error: `You can only upload ${allowedExtensions
              .join(", ")
              .toUpperCase()} files`,
          },
          status: "ERROR",
        });
        showFileNames = false;
        setDisableUpload(true);
        setFileNames([]);
        setShortFileName(null);
        return;
      }

      if (totalSizeInMB > maxFileSize) {
        const errorMessage = getFileSizeErrorMessage(totalSizeInMB);
        setFormData({ data: { error: errorMessage }, status: "ERROR" });
        showFileNames = false;
        setDisableUpload(true);
        setFileNames([]);
        setShortFileName(null);
        return;
      }

      for (let x = 0; x < value.length; x++) {
        localFileNames.push(props.filePrefix + value[x].name);
      }
      setFileNames(localFileNames);
      setShortFileName(
        Object.values(value)
          .map((file) => file.name)
          .join(", ")
      );
      setDisableUpload(false);
    }

    const name = target.name;
    if (props.filePrefix && name === "fileName") {
      setFileName(
        Object.values(value)
          .map((file) => props.filePrefix + file.name)
          .join(", ")
      );
    }

    setFormData({
      data: { ...formData.data, [name]: value },
      status: "IDLE",
    });
  };

  // Manages file upload submission, critical for orchestrating uploads and handling results
  const handleSubmit = async () => {
    if (!formData.data.fileName || formData.data.fileName.length === 0) {
      setFormData({
        data: { error: "Please select at least one file to upload" },
        status: "ERROR",
      });
      return;
    }

    const options =
      props.uploadConfig.options[props.formData[props.uploadConfig.key]];
    const allowedExtensions = options.allowedExtensions;
    const { totalSizeInMB, invalidExtension } = validateFiles(
      formData.data.fileName,
      allowedExtensions
    );

    if (invalidExtension) {
      setFormData({
        data: {
          error: `You can only upload ${allowedExtensions
            .join(", ")
            .toUpperCase()} files`,
        },
        status: "ERROR",
      });
      setDocUploading(false);
      return;
    }

    if (totalSizeInMB > maxFileSize) {
      const errorMessage = getFileSizeErrorMessage(totalSizeInMB);
      setFormData({ data: { error: errorMessage }, status: "ERROR" });
      setDocUploading(false);
      return;
    }

    setDocUploading(true);
    setFormData({ ...formData, status: "SUBMITTED" });

    const alertMessage = options.popupAlertText;
    if (alertMessage) {
      alert(alertMessage);
    }

    try {
      const files = formData.data.fileName;
      const results = [];

      // Process files sequentially
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = fileNames[i];
        const sizeInBytes = file.size;

        if (sizeInBytes <= maxFileSizeLegacy) {
          results.push(await uploadFileLegacy(file, fileName));
        } else {
          results.push(await uploadFileToGCS(file, fileName));
        }
      }

      // Check if all uploads succeeded
      if (results.every((result) => result.success)) {
        setFormData({
          data: {
            message: `Successfully uploaded ${files.length} file${
              files.length > 1 ? "s" : ""
            }!`,
          },
          status: "SUCCESS",
        });
        setTimeout(() => {
          if (props.callback) props.callback(fileNames);
          resetFileInput();
        }, 5000);
      } else {
        // Aggregate error messages from failed uploads
        const errorMessages = results
          .filter((result) => !result.success)
          .map((result) => result.error)
          .join("; ");
        setFormData({
          data: {
            error:
              errorMessages || "Some files failed to upload. Please try again.",
          },
          status: "ERROR",
        });
      }
    } catch (e) {
      console.error("Failed to upload files:", e);
      setFormData({
        data: { error: `Failed to upload files: ${e.message}` },
        status: "ERROR",
      });
    } finally {
      setDocUploading(false);
      setUploadProgress({});
    }
  };

  // Handles legacy uploads for smaller files, essential for compatibility with existing API
  const uploadFileLegacy = async (file, fileName) => {
    const uploadData = new FormData();
    uploadData.append("fileName-0", file, fileName);
    uploadData.append("id", props.id);
    uploadData.append("userId", props.userId);
    uploadData.append("objectType", props.collectionName);
    uploadData.append("agentName", props.agent_name || props.collectionName);

    const options =
      props.uploadConfig.options[props.formData[props.uploadConfig.key]];
    if (options.additionalUploadFields) {
      for (const field of options.additionalUploadFields) {
        uploadData.append(field, props.formData[field]);
      }
    }

    try {
      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: { status: "Uploading via legacy method...", progress: 0 },
      }));
      const response = await fetchSecure("/api/upload", {
        method: "POST",
        body: uploadData,
      });

      const data = await response.json();
      if (response.status === 200 && data.success) {
        setUploadProgress((prev) => ({
          ...prev,
          [fileName]: {
            status: "Upload successful! Processing in background...",
            progress: 100,
          },
        }));
        return { success: true, data };
      } else {
        setFormData({
          data: { error: data.error || "Legacy upload failed" },
          status: "ERROR",
        });
        setUploadProgress((prev) => ({
          ...prev,
          [fileName]: {
            status: `Upload failed: ${data.error || "Legacy upload failed"}`,
            progress: 0,
          },
        }));
        return { success: false, error: data.error || "Legacy upload failed" };
      }
    } catch (error) {
      setFormData({
        data: { error: `Failed to upload file: ${error.message}` },
        status: "ERROR",
      });
      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: { status: `Upload failed: ${error.message}`, progress: 0 },
      }));
      return { success: false, error: error.message };
    }
  };

  const uploadFileToGCS = async (file, fileName) => {
    try {
      // Step 1: Request signed URL
      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: { status: "Requesting secure URL...", progress: 0 },
      }));
      const response = await fetchSecure("/api/getSignedUrl", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: props.userId,
          objectId: props.id,
          objectType: props.collectionName,
          fileName,
          contentType: file.type,
          agentType: props.agent_type || "DOCUMENT_QA",
          agentName: props.agent_name || props.collectionName,
        }),
      });

      const signedUrlData = await response.json();
      if (!response.ok || !signedUrlData.success || !signedUrlData.signedUrl) {
        const errorMessage = signedUrlData.error || "Failed to get signed URL";
        setUploadProgress((prev) => ({
          ...prev,
          [fileName]: { status: `Upload failed: ${errorMessage}`, progress: 0 },
        }));
        return { success: false, error: errorMessage };
      }

      const { signedUrl, filePath } = signedUrlData;

      // Step 2: Upload to GCS
      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: {
          status: "Uploading to Cloud Storage...",
          progress: 0,
        },
      }));
      await uploadWithSignedUrl(file, fileName, signedUrl);

      // Step 3: Confirm upload
      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: { status: "Confirming upload...", progress: 90 },
      }));
      const confirmResponse = await fetchSecure("/api/confirmUpload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: props.userId,
          objectId: props.id,
          objectType: props.collectionName,
          fileName,
          filePath,
          agentType: props.agent_type || "DOCUMENT_QA",
          agentName: props.agent_name || props.collectionName,
        }),
      });

      const confirmData = await confirmResponse.json();
      if (!confirmResponse.ok || !confirmData.success) {
        const errorMessage = confirmData.error || "Failed to confirm upload";
        setUploadProgress((prev) => ({
          ...prev,
          [fileName]: { status: `Upload failed: ${errorMessage}`, progress: 0 },
        }));
        return { success: false, error: errorMessage };
      }

      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: {
          status: "Upload successful! Processing in background...",
          progress: 100,
        },
      }));
      return { success: true, data: confirmData };
    } catch (error) {
      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: { status: `Upload failed: ${error.message}`, progress: 0 },
      }));
      return { success: false, error: error.message };
    }
  };

  const uploadWithSignedUrl = (file, fileName, signedUrl) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round(
            (event.loaded * 100) / event.total
          );
          setUploadProgress((prev) => ({
            ...prev,
            [fileName]: { ...prev[fileName], progress: percentCompleted },
          }));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          const errorMessage = `Upload failed with status ${xhr.status}`;
          setUploadProgress((prev) => ({
            ...prev,
            [fileName]: {
              status: `Upload failed: ${errorMessage}`,
              progress: 0,
            },
          }));
          reject(new Error(errorMessage));
        }
      };

      xhr.onerror = () => {
        const errorMessage = "Network error during upload";
        setUploadProgress((prev) => ({
          ...prev,
          [fileName]: { status: `Upload failed: ${errorMessage}`, progress: 0 },
        }));
        reject(new Error(errorMessage));
      };
      xhr.send(file);
    });
  };

  return (
    <Container>
      <Row>
        <Col>
          <TopMargin />
          <Form>
            <Form.Group controlId="uploadFile" className="form-group">
              <Form.Label>
                Upload{" "}
                {
                  props.uploadConfig.options[
                    props.formData[props.uploadConfig.key]
                  ].displayFileExtList
                }{" "}
                File
                <small>(s)</small>
              </Form.Label>
              <div className="file-uploader-s1">
                <div className="icon">
                  <IoMdAttach />
                </div>
                <div className="text">
                  Insert file(<small>s</small>)
                </div>
                <Form.Control
                  key={fileInputKey}
                  type="file"
                  name="fileName"
                  placeholder="Select a file or files"
                  className="form-control"
                  onChange={handleInputChange}
                  multiple
                  disabled={isDocUploading}
                />
              </div>
            </Form.Group>
            {shortFileName && (
              <p style={{ color: "var(--light-color)" }}>{shortFileName}</p>
            )}
            {Object.keys(uploadProgress).map((fileName) => (
              <div key={fileName}>
                {uploadProgress[fileName].progress > 0 && (
                  <ProgressBar
                    now={uploadProgress[fileName].progress}
                    label={`${uploadProgress[fileName].progress}%`}
                  />
                )}
              </div>
            ))}
            <Form.Group />
            <RowMargin />
            <div
              style={{ display: "flex" }}
              className="agents-view-page-top-button"
            >
              <SuperAppButton
                className="primary-gradient-btn"
                type="button"
                onClick={handleSubmit}
                disabled={
                  isDocUploading || disableUpload || fileNames.length === 0
                }
              >
                Upload
                {isDocUploading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginLeft: "5px" }}
                  />
                )}
              </SuperAppButton>
              &nbsp;
              <SuperAppButton
                className="primary-gradient-btn"
                type="button"
                onClick={props.handleUploadFileCancel}
              >
                Cancel
              </SuperAppButton>
            </div>
          </Form>
        </Col>
      </Row>
      {formData.status === "ERROR" && (
        <>
          <RowMargin />
          <Row>
            <Col>
              <Alert variant="danger">
                {formData.data.error ||
                  "Failed to upload file, please try again"}
              </Alert>
            </Col>
          </Row>
        </>
      )}
      {formData.status === "SUCCESS" && (
        <>
          <RowMargin />
          <Row>
            <Col>
              <Alert variant="success">
                {formData.data.message ||
                  `Successfully uploaded ${fileNames.length} file${
                    fileNames.length > 1 ? "s" : ""
                  }!`}
              </Alert>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}
